import { useCallback, useEffect, useRef, useState } from "react";
import {
  describeGetUserMediaError,
  isMediaRecorderSupported,
  pickRecorderMimeType,
  stopMediaStream,
} from "./mediaRecorderVoice";
import { transcribeRecordedAudio } from "./transcribeRecordedAudio";

const VOICE_LANG = "en-US";

// Spoken replies are parked for now. Flip this to true to restore TTS.
const TTS_ENABLED = false;

const SERVER_ERROR_MESSAGE =
  "Oops! Something went wrong. Please try again later.";

const SILENT_ERROR_TYPES = new Set(["aborted", "stopped", "no-speech"]);

export const stripForSpeech = (text) =>
  String(text || "")
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .replace(/[*_~]/g, "")
    .replace(/\s+/g, " ")
    .trim();

export const isSpeakableAssistantText = (text) => {
  const value = String(text || "").trim();
  if (!value) return false;
  if (value === SERVER_ERROR_MESSAGE) return false;
  if (/^Error:/i.test(value)) return false;
  if (/networkerror|network error|failed to fetch/i.test(value)) return false;
  return true;
};

const getSpeechRecognitionCtor = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const isNativeRecognitionUsable = () => {
  const Ctor = getSpeechRecognitionCtor();
  if (!Ctor) return false;
  try {
    const instance = new Ctor();
    instance.abort?.();
    return true;
  } catch {
    return false;
  }
};

const describeRecognitionError = (error) => {
  switch (error) {
    case "not-allowed":
    case "service-not-allowed":
      return {
        type: "permission",
        message:
          "Microphone permission was denied. Enable it in your browser settings to use voice input.",
      };
    case "no-speech":
      return {
        type: "no-speech",
        message: "No speech was detected. Please try again.",
      };
    case "audio-capture":
      return {
        type: "audio-capture",
        message: "No microphone was found. Check that a mic is connected.",
      };
    case "network":
      return {
        type: "network",
        message:
          "Voice recognition needs a network connection. Check your internet and try again.",
      };
    case "aborted":
      return { type: "aborted", message: "" };
    default:
      return {
        type: error || "error",
        message: "Voice input failed. Please try again.",
      };
  }
};

export default function useVoiceChat({
  enabled = true,
  onTranscript,
  onFinalTranscript,
  onError,
} = {}) {
  const [recognitionSupported] = useState(isNativeRecognitionUsable);
  const [recordingFallbackSupported] = useState(isMediaRecorderSupported);
  const [ttsSupported] = useState(
    () =>
      TTS_ENABLED &&
      typeof window !== "undefined" &&
      "speechSynthesis" in window,
  );
  const voiceInputSupported = recognitionSupported || recordingFallbackSupported;

  const [status, setStatus] = useState(
    voiceInputSupported ? "idle" : "unsupported",
  );
  const [errorMessage, setErrorMessage] = useState("");

  const recognitionRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const modeRef = useRef(null);
  const statusRef = useRef(status);
  const startingRef = useRef(false);
  const submittedRef = useRef(false);
  const stoppedByUserRef = useRef(false);
  const transcribeGenerationRef = useRef(0);
  const onTranscriptRef = useRef(onTranscript);
  const onFinalTranscriptRef = useRef(onFinalTranscript);
  const onErrorRef = useRef(onError);

  statusRef.current = status;
  onTranscriptRef.current = onTranscript;
  onFinalTranscriptRef.current = onFinalTranscript;
  onErrorRef.current = onError;

  const emitError = useCallback((described) => {
    if (!described || SILENT_ERROR_TYPES.has(described.type)) {
      if (described?.type === "no-speech") {
        setStatus("idle");
        setErrorMessage(described.message || "");
      }
      return;
    }
    setStatus(described.type === "unsupported" ? "unsupported" : "error");
    setErrorMessage(described.message || "");
    onErrorRef.current?.(described);
  }, []);

  const emitFinalTranscript = useCallback((text) => {
    const trimmed = String(text || "").trim();
    if (!trimmed || submittedRef.current) return false;
    submittedRef.current = true;
    setStatus("processing");
    onFinalTranscriptRef.current?.(trimmed);
    return true;
  }, []);

  const speakRetryRef = useRef(null);
  const voicesWaitRef = useRef(null);
  const voicesListenerRef = useRef(null);
  const ttsSessionRef = useRef(0);

  const clearTtsTimers = useCallback(() => {
    if (speakRetryRef.current) {
      window.clearTimeout(speakRetryRef.current);
      speakRetryRef.current = null;
    }
    if (voicesWaitRef.current) {
      window.clearTimeout(voicesWaitRef.current);
      voicesWaitRef.current = null;
    }
    if (voicesListenerRef.current) {
      window.speechSynthesis?.removeEventListener?.(
        "voiceschanged",
        voicesListenerRef.current,
      );
      voicesListenerRef.current = null;
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    ttsSessionRef.current += 1;
    clearTtsTimers();
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setStatus((prev) => (prev === "speaking" ? "idle" : prev));
  }, [clearTtsTimers]);

  const releaseVoiceBusy = useCallback(() => {
    ttsSessionRef.current += 1;
    clearTtsTimers();
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setStatus((prev) =>
      prev === "speaking" || prev === "processing" ? "idle" : prev,
    );
  }, [clearTtsTimers]);

  const pickVoice = useCallback(() => {
    const voices = window.speechSynthesis?.getVoices?.() || [];
     return (
    voices.find(
      (voice) => voice.name === "Microsoft David - English (United States)",
    ) ||
    voices.find((voice) => voice.lang.startsWith("en")) ||
    null
  );
  }, []);

  const speak = useCallback(
    (message) => {
      if (!TTS_ENABLED || !ttsSupported) {
        emitError({
          type: "tts-unavailable",
          message: "Spoken replies are not available in this browser.",
        });
        setStatus((prev) =>
          prev === "processing" || prev === "speaking" ? "idle" : prev,
        );
        return;
      }
      const text = stripForSpeech(message);
      if (!text) {
        setStatus((prev) =>
          prev === "processing" || prev === "speaking" ? "idle" : prev,
        );
        return;
      }

      const session = ttsSessionRef.current + 1;
      ttsSessionRef.current = session;
      clearTtsTimers();
      // Mark speaking immediately so the mic can stop TTS even if the
      // browser never fires utterance.onstart (common in Chrome).
      setStatus("speaking");

      const attachUtteranceHandlers = (utterance) => {
        utterance.onstart = () => {
          if (session !== ttsSessionRef.current) return;
          setStatus("speaking");
        };
        utterance.onend = () => {
          if (session !== ttsSessionRef.current) return;
          setStatus((prev) => (prev === "speaking" ? "idle" : prev));
        };
        utterance.onerror = () => {
          if (session !== ttsSessionRef.current) return;
          setStatus((prev) => (prev === "speaking" ? "idle" : prev));
        };
      };

      const play = () => {
        if (session !== ttsSessionRef.current) return;
        if (speakRetryRef.current) {
          window.clearTimeout(speakRetryRef.current);
          speakRetryRef.current = null;
        }
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = VOICE_LANG;
      const voice = pickVoice();
      if (voice) utterance.voice = voice;
        attachUtteranceHandlers(utterance);

        window.speechSynthesis.speak(utterance);
        if (window.speechSynthesis.paused) {
          window.speechSynthesis.resume();
        }

        // Chrome can swallow the first utterance before voices are ready.
        speakRetryRef.current = window.setTimeout(() => {
          speakRetryRef.current = null;
          if (session !== ttsSessionRef.current) return;
          if (
            window.speechSynthesis.speaking ||
            window.speechSynthesis.pending
          ) {
            return;
          }
          const retry = new SpeechSynthesisUtterance(text);
          retry.lang = VOICE_LANG;
          const retryVoice = pickVoice();
          if (retryVoice) retry.voice = retryVoice;
          attachUtteranceHandlers(retry);
          window.speechSynthesis.speak(retry);
          if (window.speechSynthesis.paused) {
            window.speechSynthesis.resume();
          }
        }, 120);
      };

      const voices = window.speechSynthesis.getVoices() || [];
      if (!voices.length) {
        const onVoices = () => {
          if (voicesListenerRef.current === onVoices) {
            voicesListenerRef.current = null;
          }
          window.speechSynthesis.removeEventListener?.(
            "voiceschanged",
            onVoices,
          );
          if (voicesWaitRef.current) {
            window.clearTimeout(voicesWaitRef.current);
            voicesWaitRef.current = null;
          }
          play();
        };
        voicesListenerRef.current = onVoices;
        window.speechSynthesis.addEventListener?.("voiceschanged", onVoices);
        voicesWaitRef.current = window.setTimeout(() => {
          voicesWaitRef.current = null;
          if (voicesListenerRef.current === onVoices) {
            voicesListenerRef.current = null;
          }
          window.speechSynthesis.removeEventListener?.(
            "voiceschanged",
            onVoices,
          );
          play();
        }, 400);
        return;
      }

      play();
    },
    [clearTtsTimers, emitError, pickVoice, ttsSupported],
  );

  const cleanupRecorder = useCallback(() => {
    try {
      if (recorderRef.current?.state === "recording") {
        recorderRef.current.stop();
      }
    } catch {
      // Recorder may already be inactive.
    }
    recorderRef.current = null;
    stopMediaStream(streamRef.current);
    streamRef.current = null;
    chunksRef.current = [];
    modeRef.current = null;
  }, []);

  const stopNativeRecognition = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      // Recognition may already be stopped.
    }
  }, []);

  const transcribeRecording = useCallback(
    async (blob) => {
      const generation = transcribeGenerationRef.current;
      setStatus("processing");
      try {
        const text = await transcribeRecordedAudio(blob);
        if (generation !== transcribeGenerationRef.current) return;
        if (!text) {
          submittedRef.current = false;
          emitError({
            type: "no-speech",
            message: "No speech was detected. Please try again.",
          });
          return;
        }
        onTranscriptRef.current?.(text);
        emitFinalTranscript(text);
      } catch (error) {
        if (generation !== transcribeGenerationRef.current) return;
        submittedRef.current = false;
        const networkLike = /network|fetch|load|cdn|failed/i.test(
          String(error?.message || ""),
        );
        emitError({
          type: networkLike ? "network" : "recording-failure",
          message: networkLike
            ? "Voice recognition needs a network connection to transcribe speech in this browser."
            : "Could not transcribe the recording. Please try again.",
        });
      }
    },
    [emitError, emitFinalTranscript],
  );

  const startNativeRecognition = useCallback(() => {
    const SpeechRecognitionCtor = getSpeechRecognitionCtor();
    const recognition = new SpeechRecognitionCtor();
    recognition.lang = VOICE_LANG;
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let interim = "";
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const piece = event.results[i]?.[0]?.transcript || "";
        if (event.results[i].isFinal) finalText += piece;
        else interim += piece;
      }
      const live = (finalText || interim).trim();
      if (live) onTranscriptRef.current?.(live);
      if (finalText.trim()) emitFinalTranscript(finalText.trim());
    };

    recognition.onerror = (event) => {
      const described = describeRecognitionError(event?.error);
      if (stoppedByUserRef.current && described?.type === "aborted") {
        setStatus((prev) => (prev === "listening" ? "idle" : prev));
        return;
      }
      if (described?.type === "aborted") {
        setStatus((prev) => (prev === "listening" ? "idle" : prev));
        return;
      }
      if (submittedRef.current && described?.type === "no-speech") return;
      emitError(described);
    };

    recognition.onend = () => {
      recognitionRef.current = null;
      if (modeRef.current === "native") modeRef.current = null;
      setStatus((prev) => (prev === "listening" ? "idle" : prev));
    };

    recognitionRef.current = recognition;
    recognition.start();
    modeRef.current = "native";
    setStatus("listening");
    setErrorMessage("");
  }, [emitError, emitFinalTranscript]);

  const startMediaRecorder = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    streamRef.current = stream;
    chunksRef.current = [];

    const mimeType = pickRecorderMimeType();
    const recorder = mimeType
      ? new MediaRecorder(stream, { mimeType })
      : new MediaRecorder(stream);

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    recorder.onerror = () => {
      cleanupRecorder();
      emitError({
        type: "recording-failure",
        message: "Recording failed. Please try again.",
      });
    };

    recorder.onstop = () => {
      const chunks = chunksRef.current.slice();
      const type = recorder.mimeType || mimeType || "audio/webm";
      stopMediaStream(streamRef.current);
      streamRef.current = null;
      recorderRef.current = null;
      modeRef.current = null;
      chunksRef.current = [];

      const blob = new Blob(chunks, { type });
      if (stoppedByUserRef.current && blob.size < 800) {
        emitError({
          type: "no-speech",
          message: "No speech was detected. Please try again.",
        });
        return;
      }
      void transcribeRecording(blob);
    };

    recorderRef.current = recorder;
    modeRef.current = "recorder";
    recorder.start();
    setStatus("listening");
    setErrorMessage("");
  }, [cleanupRecorder, emitError, transcribeRecording]);

  const stopListening = useCallback(() => {
    stoppedByUserRef.current = true;
    startingRef.current = false;
    if (modeRef.current === "recorder" || recorderRef.current) {
      setStatus("processing");
      try {
        if (recorderRef.current?.state === "recording") {
          recorderRef.current.stop();
        } else {
          cleanupRecorder();
          setStatus((prev) => (prev === "processing" ? "idle" : prev));
        }
      } catch {
        cleanupRecorder();
        setStatus("idle");
      }
      return;
    }
    stopNativeRecognition();
  }, [cleanupRecorder, stopNativeRecognition]);

  const startListening = useCallback(() => {
    if (!voiceInputSupported) {
      emitError({
        type: "unsupported",
        message:
          "Voice input is not available in this browser. Microphone recording requires a current Chrome, Edge, or Firefox build on HTTPS or localhost.",
      });
      return;
    }
    if (!enabled) return;
    if (startingRef.current) return;
    if (
      statusRef.current === "listening" ||
      statusRef.current === "processing"
    ) {
      return;
    }

    stopSpeaking();
    submittedRef.current = false;
    stoppedByUserRef.current = false;
    transcribeGenerationRef.current += 1;
    startingRef.current = true;

    const start = async () => {
      try {
        if (recognitionSupported) {
          startNativeRecognition();
          return;
        }
        await startMediaRecorder();
      } catch (error) {
        try {
          recognitionRef.current?.abort();
        } catch {
          // Native recognition may not have started.
        }
        recognitionRef.current = null;
        cleanupRecorder();
        if (recordingFallbackSupported) {
          try {
            await startMediaRecorder();
            return;
          } catch (fallbackError) {
            emitError(describeGetUserMediaError(fallbackError));
            return;
          }
        }
        emitError(
          error?.name
            ? describeGetUserMediaError(error)
            : {
                type: "error",
                message: "Could not start the microphone. Please try again.",
              },
        );
      } finally {
        startingRef.current = false;
      }
    };

    void start();
  }, [
    cleanupRecorder,
    emitError,
    enabled,
    recognitionSupported,
    recordingFallbackSupported,
    startMediaRecorder,
    startNativeRecognition,
    stopSpeaking,
    voiceInputSupported,
  ]);

  useEffect(() => {
    if (!ttsSupported) return undefined;
    const warmVoices = () => {
      window.speechSynthesis.getVoices();
    };
    warmVoices();
    window.speechSynthesis.addEventListener?.("voiceschanged", warmVoices);
    return () => {
      window.speechSynthesis.removeEventListener?.("voiceschanged", warmVoices);
    };
  }, [ttsSupported]);

  useEffect(() => {
    return () => {
      transcribeGenerationRef.current += 1;
      try {
        recognitionRef.current?.abort();
      } catch {
        // Ignore abort errors during unmount.
      }
      cleanupRecorder();
      ttsSessionRef.current += 1;
      clearTtsTimers();
      if (typeof window !== "undefined") {
        window.speechSynthesis?.cancel();
      }
    };
  }, [cleanupRecorder, clearTtsTimers]);

  return {
    status,
    errorMessage,
    isRecognitionSupported: recognitionSupported,
    isTtsSupported: ttsSupported,
    isRecordingFallbackSupported: recordingFallbackSupported,
    isListening: status === "listening",
    isSpeaking: status === "speaking",
    isProcessing: status === "processing",
    startListening,
    stopListening,
    stopSpeaking,
    releaseVoiceBusy,
    speak,
  };
}
